import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaUser, FaCamera, FaSave, FaKey } from 'react-icons/fa';
import './UserProfile.css';

const UserProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    gender: '',
    dateOfBirth: '',
    addressStreet: '',
    addressCity: '',
    addressState: '',
    addressCounty: '',
    addressZip: '',
    driversLicense: '',
    driversLicenseState: '',
    driversLicenseExpiration: ''
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/users/me');
      if (response.data.success) {
        const user = response.data.data;
        setProfile(user);
        setFormData({
          firstName: user.first_name || '',
          lastName: user.last_name || '',
          email: user.email || '',
          phone: user.phone || '',
          gender: user.gender || '',
          dateOfBirth: user.date_of_birth ? user.date_of_birth.split('T')[0] : '',
          addressStreet: user.address_street || '',
          addressCity: user.address_city || '',
          addressState: user.address_state || '',
          addressCounty: user.address_county || '',
          addressZip: user.address_zip || '',
          driversLicense: user.drivers_license || '',
          driversLicenseState: user.drivers_license_state || '',
          driversLicenseExpiration: user.drivers_license_expiration ? user.drivers_license_expiration.split('T')[0] : ''
        });
        if (user.photo_url) {
          setImagePreview(`http://localhost:5000${user.photo_url}`);
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      alert('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload image
    try {
      setUploadingImage(true);
      const formData = new FormData();
      formData.append('profileImage', file);

      const response = await axios.post('/users/me/profile-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        alert('Profile image updated successfully!');
        fetchProfile(); // Refresh profile data
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert(error.response?.data?.message || 'Failed to upload image');
      // Revert preview on error
      if (profile?.photo_url) {
        setImagePreview(`http://localhost:5000${profile.photo_url}`);
      } else {
        setImagePreview(null);
      }
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const response = await axios.put('/users/me', formData);
      if (response.data.success) {
        alert('Profile updated successfully!');
        fetchProfile(); // Refresh profile data
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      alert(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }

    try {
      setSaving(true);
      const response = await axios.put('/users/me', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      if (response.data.success) {
        alert('Password changed successfully!');
        setShowPasswordModal(false);
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      }
    } catch (error) {
      console.error('Error changing password:', error);
      alert(error.response?.data?.message || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading profile...</div>;
  }

  return (
    <div className="user-profile">
      <div className="profile-header">
        <h1><FaUser /> My Profile</h1>
        <button
          className="btn btn-secondary"
          onClick={() => setShowPasswordModal(true)}
        >
          <FaKey /> Change Password
        </button>
      </div>

      <div className="profile-content">
        <div className="profile-image-section">
          <div className="profile-image-container">
            {imagePreview ? (
              <img src={imagePreview} alt="Profile" className="profile-image" />
            ) : (
              <div className="profile-image-placeholder">
                <FaUser size={80} />
              </div>
            )}
            {uploadingImage && <div className="image-uploading-overlay">Uploading...</div>}
          </div>
          <label className="upload-button">
            <FaCamera /> {imagePreview ? 'Change Photo' : 'Upload Photo'}
            <input
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              style={{ display: 'none' }}
              disabled={uploadingImage}
            />
          </label>
          <small>Max size: 5MB. Formats: JPG, PNG, GIF</small>
        </div>

        <form onSubmit={handleSaveProfile} className="profile-form">
          <div className="form-section">
            <h2>Personal Information</h2>

            <div className="form-row">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Gender</label>
                <select
                  value={formData.gender}
                  onChange={(e) => handleInputChange('gender', e.target.value)}
                >
                  <option value="">Select...</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>Date of Birth</label>
                <input
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>Address</h2>

            <div className="form-group">
              <label>Street Address</label>
              <input
                type="text"
                value={formData.addressStreet}
                onChange={(e) => handleInputChange('addressStreet', e.target.value)}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  value={formData.addressCity}
                  onChange={(e) => handleInputChange('addressCity', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>County</label>
                <input
                  type="text"
                  value={formData.addressCounty}
                  onChange={(e) => handleInputChange('addressCounty', e.target.value)}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>State</label>
                <select
                  value={formData.addressState}
                  onChange={(e) => handleInputChange('addressState', e.target.value)}
                >
                  <option value="">Select State...</option>
                  <option value="MT">Montana</option>
                  {/* Add other states as needed */}
                </select>
              </div>
              <div className="form-group">
                <label>ZIP Code</label>
                <input
                  type="text"
                  value={formData.addressZip}
                  onChange={(e) => handleInputChange('addressZip', e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>Driver's License</h2>

            <div className="form-row">
              <div className="form-group">
                <label>License Number</label>
                <input
                  type="text"
                  value={formData.driversLicense}
                  onChange={(e) => handleInputChange('driversLicense', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>License State</label>
                <select
                  value={formData.driversLicenseState}
                  onChange={(e) => handleInputChange('driversLicenseState', e.target.value)}
                >
                  <option value="">Select State...</option>
                  <option value="MT">Montana</option>
                  {/* Add other states as needed */}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Expiration Date</label>
              <input
                type="date"
                value={formData.driversLicenseExpiration}
                onChange={(e) => handleInputChange('driversLicenseExpiration', e.target.value)}
              />
            </div>
          </div>

          <div className="form-section">
            <h2>Account Information</h2>
            <div className="readonly-info">
              <div className="info-item">
                <label>Username:</label>
                <span>{profile?.username}</span>
              </div>
              <div className="info-item">
                <label>Role:</label>
                <span className={`role-badge role-${profile?.role}`}>{profile?.role}</span>
              </div>
              <div className="info-item">
                <label>Account Created:</label>
                <span>{profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : 'N/A'}</span>
              </div>
              <div className="info-item">
                <label>Last Login:</label>
                <span>{profile?.last_login ? new Date(profile.last_login).toLocaleString() : 'Never'}</span>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              <FaSave /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>

      {showPasswordModal && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><FaKey /> Change Password</h2>
              <button className="close-btn" onClick={() => setShowPasswordModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleChangePassword} className="password-form">
              <div className="form-group">
                <label>Current Password *</label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                  required
                  minLength="6"
                />
              </div>
              <div className="form-group">
                <label>New Password *</label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                  required
                  minLength="6"
                />
                <small>Minimum 6 characters</small>
              </div>
              <div className="form-group">
                <label>Confirm New Password *</label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                  required
                  minLength="6"
                />
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowPasswordModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile;
